import { useEffect, useRef, useState } from 'react';
import { useColor } from 'components/ui/ColorContext';

interface HudTransitionProps {
    onComplete: () => void;
    labelTop?: string;
    labelMain?: string;
    labelSub?: string;
}

export const HudTransition = ({
    onComplete,
    labelTop = 'SYNCING',
    labelMain = 'QUEST COMPLETE',
    labelSub = 'Initialising rewards...',
}: HudTransitionProps) => {
    const [isExiting, setIsExiting] = useState(false);
    const [isClimax, setIsClimax] = useState(false);
    const [progress, setProgress] = useState(0);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>(0);
    const burstTriggeredRef = useRef(false);

    const { primaryColor } = useColor();

    const hexToRgb = (hex: string) => {
        const clean = hex.replace("#", "");
        const bigint = parseInt(clean, 16);

        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return `${r}, ${g}, ${b}`;
    };

    const rgb = hexToRgb(primaryColor);

    // Particle system with burst effect
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = Math.max(1, window.devicePixelRatio || 1);
        let w = 0, h = 0;
        const particles: Array<{
            x: number;
            y: number;
            vx: number;
            vy: number;
            size: number;
            alpha: number;
            life: number;
            maxLife: number;
            isBurst?: boolean;
        }> = [];

        const resize = () => {
            const rect = canvas.parentElement?.getBoundingClientRect();
            if (!rect) return;
            w = Math.floor(rect.width);
            h = Math.floor(rect.height);
            canvas.width = Math.floor(w * dpr);
            canvas.height = Math.floor(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        resize();
        window.addEventListener('resize', resize);

        const spawnParticle = () => {
            const cx = w / 2;
            const cy = h / 2;
            const angle = Math.random() * Math.PI * 2;
            const dist = 40 + Math.random() * 60;
            particles.push({
                x: cx + Math.cos(angle) * dist,
                y: cy + Math.sin(angle) * dist,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5 - 0.3,
                size: 1 + Math.random() * 2,
                alpha: 0.4 + Math.random() * 0.4,
                life: 0,
                maxLife: 60 + Math.random() * 60,
            });
        };

        // Spawn burst particles for climax
        const spawnBurstParticles = () => {
            if (burstTriggeredRef.current) return;
            burstTriggeredRef.current = true;

            const cx = w / 2;
            const cy = h / 2;
            const burstCount = 40;

            for (let i = 0; i < burstCount; i++) {
                const angle = (i / burstCount) * Math.PI * 2;
                const speed = 4 + Math.random() * 6;
                particles.push({
                    x: cx,
                    y: cy,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    size: 2 + Math.random() * 3,
                    alpha: 0.8 + Math.random() * 0.2,
                    life: 0,
                    maxLife: 30 + Math.random() * 20,
                    isBurst: true,
                });
            }
        };

        let frame = 0;
        const animate = () => {
            ctx.clearRect(0, 0, w, h);

            if (frame % 3 === 0 && particles.length < 50) {
                spawnParticle();
            }

            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;

                // Burst particles slow down
                if (p.isBurst) {
                    p.vx *= 0.96;
                    p.vy *= 0.96;
                }

                p.life++;

                const lifeRatio = p.life / p.maxLife;
                const alpha = p.alpha * (1 - lifeRatio);

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

                // Burst particles are brighter white-cyan
                if (p.isBurst) {
                    ctx.fillStyle = `hsla(${rgb}, 80%, ${70 + (1 - lifeRatio) * 30}%, ${alpha})`;
                } else {
                    const hue = getComputedStyle(document.documentElement)
                        .getPropertyValue("--dynamic-primary-hue");

                    ctx.fillStyle = `rgba(${rgb},${alpha})`;
                }
                ctx.fill();

                if (p.life >= p.maxLife) {
                    particles.splice(i, 1);
                }
            }

            frame++;
            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        // Expose burst trigger
        if (isClimax) {
            spawnBurstParticles();
        }

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationRef.current);
        };
    }, [isClimax]);

    // Progress animation
    useEffect(() => {
        const duration = 1200;
        const start = performance.now();

        const updateProgress = () => {
            const elapsed = performance.now() - start;
            const newProgress = Math.min(1, elapsed / duration);
            setProgress(newProgress);

            if (newProgress < 1) {
                requestAnimationFrame(updateProgress);
            }
        };

        requestAnimationFrame(updateProgress);
    }, []);

    // Transition timeline - extended for bigger impact
    useEffect(() => {
        const enterMs = 720;
        const holdMs = 900;

        // Trigger climax burst 100ms before exit
        const climaxTimer = setTimeout(() => {
            setIsClimax(true);
        }, enterMs + holdMs - 100);

        const exitTimer = setTimeout(() => {
            setIsExiting(true);
        }, enterMs + holdMs);

        const completeTimer = setTimeout(() => {
            onComplete();
        }, enterMs + holdMs + 400);

        return () => {
            clearTimeout(climaxTimer);
            clearTimeout(exitTimer);
            clearTimeout(completeTimer);
        };
    }, [onComplete]);

    const circumference = 2 * Math.PI * 38;
    const strokeOffset = circumference * (1 - progress);

    return (
        <div
            className={`hud-transition fixed inset-0 z-[100] pointer-events-none ${isExiting ? 'is-exiting' : ''} ${isClimax ? 'is-climax' : ''}`}
        >
            {/* Backdrop */}
            <div className="hud-backdrop absolute inset-0 bg-black/95" />

            {/* Vignette */}
            <div className="hud-vignette absolute inset-0" />

            {/* Scanlines */}
            <div className="hud-scanlines absolute inset-0" />

            {/* Noise texture */}
            <div className="hud-noise absolute inset-0" />

            {/* Particles canvas */}
            <canvas
                ref={canvasRef}
                className="hud-particles absolute inset-0 w-full h-full"
                style={{ opacity: 0.8 }}
            />

            {/* Core wrapper */}
            <div className="hud-wrap absolute inset-0 flex items-center justify-center">
                {/* Glow */}
                <div className="hud-glow" />

                {/* Shockwave */}
                <div className="hud-shockwave" />

                {/* Rings SVG - Responsive sizing */}
                <svg className="hud-rings absolute w-[min(480px,85vw)] h-[min(480px,85vw)]" viewBox="0 0 200 200">
                    <defs>
                        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="1.6" result="b" />
                            <feColorMatrix in="b" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.85 0" result="g" />
                            <feMerge>
                                <feMergeNode in="g" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* Outer rings - increased stroke width for bigger impact */}
                    <circle className="ring r1" cx="100" cy="100" r="98" fill="none" stroke={`rgba(${rgb},0.12)`} strokeWidth="0.3" filter="url(#softGlow)" />
                    <circle className="ring r1" cx="100" cy="100" r="95" fill="none" stroke={`rgba(${rgb},0.2)`} strokeWidth="0.6" filter="url(#softGlow)" />
                    <circle className="ring r2 spin-slow" cx="100" cy="100" r="88" fill="none" stroke={`rgba(${rgb},0.3)`} strokeWidth="1" strokeDasharray="5 10" filter="url(#softGlow)" />
                    <circle className="ring r3 spin-med spin-rev" cx="100" cy="100" r="78" fill="none" stroke={`rgba(${rgb},0.25)`} strokeWidth="0.8" strokeDasharray="3 15" filter="url(#softGlow)" />
                    <circle className="ring r4" cx="100" cy="100" r="65" fill="none" stroke={`rgba(${rgb},0.4)`} strokeWidth="1.2" filter="url(#softGlow)" />

                    {/* Tick marks ring - premium instrumentation feel */}
                    {Array.from({ length: 36 }).map((_, i) => {
                        const angle = (i * 10 * Math.PI) / 180;
                        const innerR = 91;
                        const outerR = i % 3 === 0 ? 96 : 93;
                        return (
                            <line
                                key={i}
                                className="ring r2"
                                x1={100 + Math.cos(angle) * innerR}
                                y1={100 + Math.sin(angle) * innerR}
                                x2={100 + Math.cos(angle) * outerR}
                                y2={100 + Math.sin(angle) * outerR}
                                stroke={`rgba(${rgb},${i % 3 === 0 ? 0.5 : 0.25})`}
                                strokeWidth={i % 3 === 0 ? '1' : '0.5'}
                                filter="url(#softGlow)"
                            />
                        );
                    })}
                </svg>

                {/* Progress arc - larger */}
                <svg className="hud-progress absolute w-[min(160px,35vw)] h-[min(160px,35vw)]" viewBox="0 0 80 80">
                    {/* Track */}
                    <circle
                        cx="40" cy="40" r="38"
                        fill="none"
                        stroke={`rgba(${rgb},0.15)`}
                        strokeWidth="2.5"
                    />
                    {/* Progress */}
                    <circle
                        cx="40" cy="40" r="38"
                        fill="none"
                        stroke={`rgb(${rgb})`}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeOffset}
                        transform="rotate(-90 40 40)"
                        className="transition-all duration-100"
                    />
                </svg>

                {/* Text content */}
                <div className="hud-centre absolute flex flex-col items-center justify-center text-center px-4">
                    <span className="hud-label-top text-sm font-orbitron tracking-[0.35em] uppercase mb-3" style={{ color: primaryColor }}>
                        {labelTop}
                    </span>
                    <span className="hud-label-main text-2xl md:text-3xl font-orbitron font-bold tracking-wider text-glow-strong" style={{ color: primaryColor }}>
                        {labelMain}
                    </span>
                    <span className="hud-label-sub text-sm font-rubik mt-3" style={{ color: primaryColor }}>
                        {labelSub}
                    </span>
                </div>
            </div>

            {/* Flash overlay */}
            <div className="hud-flash absolute inset-0" />

            {/* Iris cut */}
            <div className="hud-cut absolute inset-0" />
        </div>
        
    );
};
